import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import {
  Phone, Mail, Globe, MapPin, FileText, UserPlus, Send, QrCode, X, Loader2,
} from "lucide-react";
import { IconLinkedIn, IconWhatsApp, IconInstagram, IconFacebook, IconTikTok, IconYouTube, IconGoogle } from "@/components/site/BrandIcons";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { LanguageSelector } from "@/components/site/LanguageSelector";
import { Seo } from "@/components/site/Seo";
import { useI18n } from "@/i18n/I18nContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const withProto = (u) => (u && !/^https?:\/\//i.test(u) ? `https://${u}` : u);
const digits = (n) => (n || "").replace(/[^0-9+]/g, "");

function ContactRow({ icon: Icon, label, value, href, testid, color }) {
  if (!value) return null;
  return (
    <a href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={testid}
      className="flex items-center gap-3.5 px-4 py-3.5 rounded-2xl bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors duration-300">
      <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}22`, color }}>
        <Icon className="w-5 h-5" />
      </span>
      <span className="min-w-0">
        <span className="block text-[11px] uppercase tracking-wide text-slate-400">{label}</span>
        <span className="block text-slate-900 truncate">{value}</span>
      </span>
    </a>
  );
}

function Social({ icon: Icon, href, label, testid }) {
  if (!href) return null;
  return (
    <a href={withProto(href)} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={testid}
      className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 hover:bg-slate-200 hover:-translate-y-0.5 transition-all duration-300">
      <Icon className="w-5 h-5" />
    </a>
  );
}

function ContactRequestModal({ slug, primary, onClose }) {
  const { t } = useI18n();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error(t("fiche.form.consentError"));
    setLoading(true);
    try {
      await api.post(`/public/profile/${slug}/contact-request`, form);
      toast.success(t("fiche.form.success"));
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300";

  return (
    <div className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="contact-request-modal">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="font-display font-bold text-lg text-slate-900">{t("fiche.form.title")}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" data-testid="contact-request-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3.5">
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder={t("fiche.form.firstName")} value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="cr-first-name" />
            <input required placeholder={t("fiche.form.lastName")} value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="cr-last-name" />
          </div>
          <input required placeholder={t("fiche.form.phone")} value={form.phone} onChange={set("phone")} className={inputCls} data-testid="cr-phone" />
          <input required type="email" placeholder={t("fiche.form.email")} value={form.email} onChange={set("email")} className={inputCls} data-testid="cr-email" />
          <textarea placeholder={t("fiche.form.message")} rows={3} value={form.message} onChange={set("message")} className={inputCls} data-testid="cr-message" />
          <label className="flex items-start gap-3 text-sm text-slate-500 cursor-pointer">
            <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 accent-cyan-500 w-4 h-4" data-testid="cr-consent" />
            <span>{t("fiche.form.consent")}</span>
          </label>
          <button type="submit" disabled={loading} data-testid="cr-submit"
            className="w-full text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-60 transition-opacity duration-300" style={{ background: primary }}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("fiche.form.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Fiche() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const { t } = useI18n();
  const source = params.get("source") || "direct";
  const [state, setState] = useState({ loading: true, profile: null, error: null });
  const [showQr, setShowQr] = useState(false);
  const [showContact, setShowContact] = useState(false);

  useEffect(() => {
    let active = true;
    api.get(`/public/profile/${slug}?source=${encodeURIComponent(source)}`)
      .then(({ data }) => { if (active) setState({ loading: false, profile: data, error: null }); })
      .catch((e) => { if (active) setState({ loading: false, profile: null, error: e.response?.status === 404 ? "notfound" : "error" }); });
    return () => { active = false; };
  }, [slug, source]);

  if (state.loading) {
    return <div className="min-h-screen bg-[#eef1f5] flex items-center justify-center"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div>;
  }

  if (state.error === "notfound" || (state.profile && !state.profile.available && state.profile.reason !== "inactive")) {
    return (
      <div className="min-h-screen bg-[#eef1f5] flex flex-col items-center justify-center text-center px-5">
        <div className="absolute top-5 right-5"><LanguageSelector /></div>
        <Logo size="text-3xl" dark />
        <p className="mt-6 text-slate-500">{t("fiche.notFound")}</p>
      </div>
    );
  }

  if (state.profile && !state.profile.available) {
    return (
      <div className="min-h-screen bg-[#eef1f5] flex flex-col items-center justify-center text-center px-5">
        <div className="absolute top-5 right-5"><LanguageSelector /></div>
        <Logo size="text-3xl" dark />
        <p className="mt-6 text-lg text-slate-900 font-display">{t("fiche.unavailable")}</p>
        <p className="mt-2 text-slate-500 text-sm">{t("fiche.unavailableSub")}</p>
      </div>
    );
  }

  const d = state.profile.data;
  const primary = d.primary_color || "#06B6D4";
  const border = d.border_color || "#2563EB";
  const fullName = `${d.first_name || ""} ${d.last_name || ""}`.trim();
  const vcardUrl = `${BACKEND_URL}/api/public/profile/${slug}/vcard`;
  const shareUrl = `${window.location.origin}/${slug}?source=qr`;
  const hasContact = d.phone || d.phone2 || d.email || d.email2 || d.website || d.website2 || d.address;
  const hasSocial = d.linkedin || d.whatsapp || d.instagram || d.facebook || d.tiktok || d.youtube;

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: fullName, url: `${window.location.origin}/${slug}` }); } catch {}
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/${slug}`);
      toast.success(t("fiche.linkCopied"));
    }
  };

  return (
    <div className="min-h-screen bg-[#eef1f5] py-6 px-4 sm:px-0" data-testid="fiche-page">
      <Seo noindex title={`${fullName || "Profil"} | BBR.GO`} path={`/${slug}`} />
      <div className="max-w-md mx-auto flex justify-end mb-3"><LanguageSelector /></div>
      <div className="max-w-md mx-auto rounded-[2rem] overflow-hidden bg-white" style={{ border: `1.5px solid ${border}`, boxShadow: `0 30px 80px -40px ${border}55` }}>
        <div className="relative h-40" style={{ background: d.cover_url ? undefined : `linear-gradient(120deg, ${primary}, ${border})` }}>
          {d.cover_url && <img src={fileUrl(d.cover_url)} alt="" className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white" />
        </div>

        <div className="bg-white px-6 pb-8 -mt-14 relative">
          <div className="w-28 h-28 rounded-full mx-auto ring-4 ring-white shadow-md overflow-hidden flex items-center justify-center bg-white" style={{ background: `${primary}22` }} data-testid="fiche-avatar">
            {d.profile_photo_url
              ? <img src={fileUrl(d.profile_photo_url)} alt={fullName} className="w-full h-full object-cover" />
              : <span className="font-display font-bold text-3xl" style={{ color: primary }}>{(d.first_name?.[0] || "") + (d.last_name?.[0] || "")}</span>}
          </div>

          <div className="text-center mt-4">
            <h1 className="font-display font-bold text-2xl text-slate-900" data-testid="fiche-name">{fullName || state.profile.company_name}</h1>
            {d.job_title && <p className="text-sm mt-1 font-medium" style={{ color: primary }} data-testid="fiche-job">{d.job_title}</p>}
            {d.company_name && <p className="text-sm text-slate-500 mt-0.5" data-testid="fiche-company">{d.company_name}</p>}
            {d.description && <p className="text-sm text-slate-500 mt-4 leading-relaxed" data-testid="fiche-description">{d.description}</p>}
          </div>

          <div className="mt-6 space-y-3">
            <a href={vcardUrl} data-testid="fiche-add-contact"
              className="w-full text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 hover:opacity-90 transition-opacity duration-300" style={{ background: primary }}>
              <UserPlus className="w-5 h-5" /> {t("fiche.addContact")}
            </a>
            <button onClick={() => setShowContact(true)} data-testid="fiche-send-coords"
              className="w-full bg-white border border-slate-200 text-slate-900 font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors duration-300">
              <Send className="w-5 h-5" /> {t("fiche.sendCoords")}
            </button>
          </div>

          {hasContact && (
            <div className="mt-6 space-y-2.5" data-testid="fiche-contacts">
              <ContactRow icon={Phone} label={t("fiche.phone")} value={d.phone} href={`tel:${digits(d.phone)}`} color={primary} testid="fiche-phone" />
              <ContactRow icon={Phone} label={t("fiche.phone2")} value={d.phone2} href={`tel:${digits(d.phone2)}`} color={primary} testid="fiche-phone2" />
              <ContactRow icon={Mail} label={t("fiche.email")} value={d.email} href={`mailto:${d.email}`} color={primary} testid="fiche-email" />
              <ContactRow icon={Mail} label={t("fiche.email2")} value={d.email2} href={`mailto:${d.email2}`} color={primary} testid="fiche-email2" />
              <ContactRow icon={Globe} label={t("fiche.website")} value={d.website} href={withProto(d.website)} color={primary} testid="fiche-website" />
              <ContactRow icon={Globe} label={t("fiche.website2")} value={d.website2} href={withProto(d.website2)} color={primary} testid="fiche-website2" />
              <ContactRow icon={MapPin} label={t("fiche.address")} value={d.address} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(d.address || "")}`} color={primary} testid="fiche-address" />
            </div>
          )}

          {hasSocial && (
            <div className="mt-6 flex flex-wrap justify-center gap-3" data-testid="fiche-socials">
              <Social icon={IconLinkedIn} href={d.linkedin} label="LinkedIn" testid="fiche-linkedin" />
              <Social icon={IconWhatsApp} href={d.whatsapp ? `https://wa.me/${digits(d.whatsapp)}` : ""} label="WhatsApp" testid="fiche-whatsapp" />
              <Social icon={IconInstagram} href={d.instagram} label="Instagram" testid="fiche-instagram" />
              <Social icon={IconFacebook} href={d.facebook} label="Facebook" testid="fiche-facebook" />
              <Social icon={IconTikTok} href={d.tiktok} label="TikTok" testid="fiche-tiktok" />
              <Social icon={IconYouTube} href={d.youtube} label="YouTube" testid="fiche-youtube" />
            </div>
          )}

          {(d.google || d.google_reviews || d.pdf_url) && (
            <div className="mt-6 space-y-2.5">
              <ContactRow icon={IconGoogle} label={t("fiche.google")} value={d.google ? t("fiche.googleView") : ""} href={withProto(d.google)} color={primary} testid="fiche-google" />
              <ContactRow icon={IconGoogle} label={t("fiche.reviews")} value={d.google_reviews ? t("fiche.reviewsAction") : ""} href={withProto(d.google_reviews)} color={primary} testid="fiche-reviews" />
              <ContactRow icon={FileText} label={t("fiche.pdf")} value={d.pdf_url ? t("fiche.pdfAction") : ""} href={d.pdf_url ? fileUrl(d.pdf_url) : undefined} color={primary} testid="fiche-pdf" />
            </div>
          )}

          <div className="mt-8 flex flex-col items-center">
            <button onClick={() => setShowQr(true)} data-testid="fiche-qr-open" className="p-4 rounded-2xl bg-white border border-slate-200 shadow-sm">
              <QRCodeSVG value={shareUrl} size={120} bgColor="#ffffff" fgColor="#0a0a12" level="M" />
            </button>
            <p className="mt-3 text-xs text-slate-400">{t("fiche.scanToOpen")}</p>
            <button onClick={share} className="mt-2 text-xs text-slate-500 hover:text-slate-800 transition-colors duration-300 flex items-center gap-1" data-testid="fiche-share">
              <QrCode className="w-3.5 h-3.5" /> {t("fiche.share")}
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-200 flex items-center justify-center">
            <Link to="/" className="opacity-70 hover:opacity-100 transition-opacity duration-300"><Logo size="text-sm" dark /></Link>
          </div>
        </div>
      </div>

      {showQr && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowQr(false)} data-testid="fiche-qr-modal">
          <div className="bg-white rounded-3xl p-8 flex flex-col items-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <QRCodeSVG value={shareUrl} size={240} level="M" />
            <p className="mt-4 text-slate-900 font-semibold">{fullName}</p>
          </div>
        </div>
      )}

      {showContact && <ContactRequestModal slug={slug} primary={primary} onClose={() => setShowContact(false)} />}
    </div>
  );
}
